// posthog-js reads campaign params once, from the first page load's URL. A
// logged-out marketing click loads /login?redirect=<path>, where the utm_*
// params hide inside `redirect`, so every post-login $pageview went out with
// utm_* NULL. Reading them off each pageview's URL fixes that.
const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
] as const

export type UtmKey = (typeof UTM_KEYS)[number]
export type UtmParams = Partial<Record<UtmKey, string>>

export function utmParamsFromSearch(search: string): UtmParams {
  const params = new URLSearchParams(search)
  const utm: UtmParams = {}
  for (const key of UTM_KEYS) {
    const value = params.get(key)?.trim()
    if (value) utm[key] = value
  }
  return utm
}

const VISIT_UTM_KEY = 'visitUtmParams'

export function rememberVisitUtmParams(search: string): void {
  const utm = landingUtmParams(search)
  if (Object.keys(utm).length === 0) return
  try {
    sessionStorage.setItem(VISIT_UTM_KEY, new URLSearchParams(utm).toString())
  } catch {
    // sessionStorage blocked (private mode): the visit just isn't remembered.
  }
}

// A logged-out click lands on /login?redirect=<tagged path>. Password login
// returns to that path, but SSO login never does, so the campaign is read from
// inside `redirect` here or it would be lost.
function landingUtmParams(search: string): UtmParams {
  const own = utmParamsFromSearch(search)
  if (Object.keys(own).length > 0) return own
  const redirect = new URLSearchParams(search).get('redirect')
  if (!redirect) return {}
  try {
    return utmParamsFromSearch(
      new URL(redirect, 'https://placeholder.invalid').search,
    )
  } catch {
    return {}
  }
}

export function visitUtmParams(): UtmParams {
  try {
    return utmParamsFromSearch(sessionStorage.getItem(VISIT_UTM_KEY) ?? '')
  } catch {
    return {}
  }
}

export function initialUtmPersonProperties(
  utm: UtmParams,
): Partial<Record<`$initial_${UtmKey}`, string>> {
  return Object.fromEntries(
    Object.entries(utm).map(([key, value]) => [`$initial_${key}`, value]),
  )
}
